import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsJson, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import * as yup from 'yup';
import { useDonationSummaryQuery } from '../../../../queries';
import { useDonationReportingChartsQuery } from '../../../../queries/Donation/useDonationReportingCharts';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { ROUTES } from '../../../constants/routes';
import { baseStyleSheet } from '../../../styles/styles';
import { DataDisplay } from '../components/DataDisplay';
import { formatCurrency } from '../helpers/formatCurrency';
import { styleSheet as advancedReportingStyleSheet } from '../styles';
import { DonationAmountDistributionChart } from './components/DonationAmountDistributionChart';
import { DonationsByCampaignChart } from './components/DonationsByCampaignChart';
import { DonationsByTagChart } from './components/DonationsByTagChart';
import { DonationsFilters } from './components/DonationsFilters';
import { DonationsOverTimeChart } from './components/DonationsOverTimeChart';

const charts: Api.IDonationChartConfiguration[] = [
	{
		key: 'amountDistribution',
		title: 'Amount Distribution',
		groupBy: 'AmountRange',
		aggregation: 'Count',
		sortField: 'Label',
		sortAscending: true,
		visualizationType: 'Bar',
	},
	{
		key: 'byCampaign',
		title: 'By Campaign',
		groupBy: 'Campaign',
		aggregation: 'Sum',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Bar',
	},
	{
		key: 'byTag',
		title: 'By Tag',
		groupBy: 'Tag',
		aggregation: 'Sum',
		limit: 10,
		sortField: 'Value',
		sortAscending: false,
		visualizationType: 'Pie',
	},
	{
		key: 'overTime',
		title: 'Donations Over Time',
		groupBy: 'Date',
		dateGranularity: 'Month',
		aggregation: 'Sum',
		sortField: 'Label',
		sortAscending: true,
		visualizationType: 'Line',
	},
];

const createCriteriaSchema = (): yup.AnySchema => {
	return yup.array().of(
		yup.object({
			property: yup.string().optional(),
			op: yup.string().optional(),
			value: yup.string().optional(),
		})
	);
};

const criteriaSchema = createCriteriaSchema();

const validateFilterCriteria = (value: unknown): Api.IDonationFilterCriteria[] | null => {
	try {
		const validated = criteriaSchema.validateSync(value, { strict: false, abortEarly: false });
		return validated as unknown as Api.IDonationFilterCriteria[];
	} catch {
		return null;
	}
};

const normalizeCriteria = (criteria: Api.IDonationFilterCriteria[]): Api.IDonationFilterCriteria[] => {
	return criteria.map(c => {
		const normalized: Api.IDonationFilterCriteria = {};
		if (c.property) normalized.property = c.property;
		if (c.op) normalized.op = c.op;
		if (c.value) normalized.value = c.value;
		if (c.criteria && c.criteria.length > 0) {
			normalized.criteria = normalizeCriteria(c.criteria);
		}
		return normalized;
	});
};

export const DonationsAdvancedReporting = () => {
	const [filterCriteria, setFilterCriteria] = useQueryState(
		'filterCriteria',
		parseAsJson<Api.IDonationFilterCriteria[]>(validateFilterCriteria).withDefault([])
	);

	const normalizedCriteria = useMemo(() => normalizeCriteria(filterCriteria || []), [filterCriteria]);
	const isUpdatingRef = React.useRef(false);
	const previousCriteriaStringRef = React.useRef<string>('');

	const donationReportingChartsQuery = useDonationReportingChartsQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IDonationFilterRequest,
		charts,
	});

	const chartsData = donationReportingChartsQuery.data;

	const handleFilterChange = React.useCallback(
		(newCriteria: Api.IDonationFilterCriteria[]) => {
			if (isUpdatingRef.current) return;

			const normalized = normalizeCriteria(newCriteria);
			const criteriaString = JSON.stringify(normalized);

			if (criteriaString !== previousCriteriaStringRef.current) {
				isUpdatingRef.current = true;
				previousCriteriaStringRef.current = criteriaString;
				setFilterCriteria(normalized.length > 0 ? normalized : null);

				// Reset the flag after a short delay to allow URL update to complete
				setTimeout(() => {
					isUpdatingRef.current = false;
				}, 0);
			}
		},
		[setFilterCriteria]
	);

	React.useEffect(() => {
		const criteriaString = JSON.stringify(normalizedCriteria);
		if (criteriaString !== previousCriteriaStringRef.current && !isUpdatingRef.current) {
			previousCriteriaStringRef.current = criteriaString;
		}
	}, [normalizedCriteria]);

	const donationSummaryQuery = useDonationSummaryQuery({
		enabled: true,
		filterRequest: {
			criteria: normalizedCriteria,
		} as Api.IDonationFilterRequest,
	});

	// const { exportToPdf, isExporting } = useExportToPdf();

	// const handleExportToPdf = React.useCallback(() => {
	// 	const filename = `donations-report-${new Date().toISOString().split('T')[0]}.pdf`;
	// 	exportToPdf('exportable-content', filename);
	// }, [exportToPdf]);

	const amountPerDonor = useMemo(() => {
		if (!donationSummaryQuery?.data || donationSummaryQuery?.data?.totalDonors === 0) {
			return 0;
		}

		return formatCurrency(donationSummaryQuery?.data?.totalAmount / donationSummaryQuery?.data?.totalDonors);
	}, [donationSummaryQuery?.data]);

	const averageDonationAmount = useMemo(() => {
		if (!donationSummaryQuery?.data || donationSummaryQuery?.data?.totalAmount === 0) {
			return 0;
		}

		return formatCurrency(donationSummaryQuery?.data?.totalAmount / donationSummaryQuery?.data?.totalDonations);
	}, [donationSummaryQuery?.data]);

	// { label: 'Data Boards', path: ROUTES.dataBoards.home },
	// 						{ label: 'Donations', path: ROUTES.dataBoards.donations },
	return (
		<Page>
			<div className={css(advancedReportingStyleSheet.pageContainer)}>
				<MultiContainerHeader
					appBarHeader={
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={ROUTES.dataBoards.home}>
											<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Data Boards</h1>
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={ROUTES.dataBoards.donations}>
											<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Donations</h1>
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage>Advanced Reporting</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					}
				/>

				<div className={css(advancedReportingStyleSheet.filterSection)}>
					<div className={css(advancedReportingStyleSheet.filterHeader)}>
						<DonationsFilters filterCriteria={normalizedCriteria} onFilterChange={handleFilterChange} />
					</div>
				</div>

				<div id='exportable-content'>
					<div className={css(advancedReportingStyleSheet.container)}>
						<DataDisplay
							title='Total Donations'
							data={donationSummaryQuery?.data?.totalDonations ?? 0}
							loading={donationSummaryQuery?.isLoading}
						/>
						<DataDisplay
							title='Total Donors'
							data={donationSummaryQuery?.data?.totalDonors ?? 0}
							loading={donationSummaryQuery?.isLoading}
						/>
						<DataDisplay
							title='Total Amount'
							data={formatCurrency(donationSummaryQuery?.data?.totalAmount ?? 0)}
							loading={donationSummaryQuery?.isLoading}
						/>

						<DataDisplay
							title='Amount Per Donor'
							data={amountPerDonor}
							loading={donationSummaryQuery?.isLoading}
							className={css(advancedReportingStyleSheet.bigDataDisplay)}
						/>

						<DataDisplay
							title='Average Donation Amount'
							data={averageDonationAmount}
							loading={donationSummaryQuery?.isLoading}
							className={css(advancedReportingStyleSheet.bigDataDisplay)}
						/>
					</div>

					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<DonationsOverTimeChart data={chartsData?.overTime} isLoading={donationReportingChartsQuery.isLoading} />
						<DonationsByCampaignChart
							data={chartsData?.byCampaign}
							isLoading={donationReportingChartsQuery.isLoading}
						/>
					</div>

					<div className={css(advancedReportingStyleSheet.chartsSection)}>
						<DonationsByTagChart data={chartsData?.byTag} isLoading={donationReportingChartsQuery.isLoading} />
						<DonationAmountDistributionChart
							data={chartsData?.amountDistribution}
							isLoading={donationReportingChartsQuery.isLoading}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};
